import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Alert, Stack, Chip, CircularProgress, Link, Divider, Tabs, Tab, Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getProductInventory, getProjectsBoard } from '../api/featureMap';
import RelatedProjectsList from '../components/products/RelatedProjectsList';
import ServiceHostsList from '../components/products/ServiceHostsList';
import ProductReachabilityTab from '../components/products/ProductReachabilityTab';
import { ComplianceFailList, SecurityAlertList, UsageCountryList } from '../components/products/ProductAxisDetail';
import ProductSecurityCard from '../components/products/ProductSecurityCard';
import ProductScopeCard from '../components/products/ProductScopeCard';
import ProductComplianceCard from '../components/products/ProductComplianceCard';
import ProductUsageCard from '../components/products/ProductUsageCard';
import ProductReleasesTab from '../components/products/ProductReleasesTab';

// 탭 순서 = 보는 순서. 문제가 있을 가능성이 높은 축을 앞에 둔다.
const TABS = ['보안', '규제 준수', '사용량', '지역 도달성', '도메인', '프로젝트', '릴리즈'];

const statusColor = {
  planned: 'default',
  'in-development': 'warning',
  ga: 'success',
  maintenance: 'info',
  eol: 'error',
};

const categoryLabel = {
  core: '자사 제품',
  localized: '지역화 제품',
  utility: '보조 / 유틸리티',
  partner: 'B2B 파트너',
};

// FEAT-004 Phase 3 v2 — Product 상세 페이지. 영역 A (SW 매핑 편집) 인터랙티브.
// FEAT-006 Phase B — 영역 C (관련 Feature) derived 표시 추가. 영역 B (Roadmap) 는 FEAT-005 대기.
export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [allProducts, setAllProducts] = useState([]);
  // #108 Phase 1 — 제품 축 대시보드. 보안·규제는 각각 독립 로딩 —
  // 한 축이 실패해도 페이지 전체를 막지 않는다 (영역 C/D 와 같은 분리 패턴).
  const [securityState, setSecurityState] = useState({ data: null, error: null, loading: true });
  const [complianceState, setComplianceState] = useState({ data: null, error: null, loading: true });
  const [usageState, setUsageState] = useState({ data: null, error: null, loading: true });
  // FEAT-020 — 지역 도달성. 다른 축과 같은 분리 로딩: 실패해도 나머지 탭을 막지 않는다.
  const [reachState, setReachState] = useState({ data: null, error: null, loading: true });
  // FEAT-018 — 프로젝트 탭 소스 (차터 + GitHub Project 진행 현황).
  const [board, setBoard] = useState({
    items: [], loading: true, error: null, progressAvailable: true, progressError: null,
  });
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // #108 Phase 1 — 보안 · 규제 축. 전사 화면과 동일한 API 를 제품 id 로 거른다.
    setSecurityState({ data: null, error: null, loading: true });
    fetch('/api/security/portfolio')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { if (!cancelled) setSecurityState({ data: j, error: null, loading: false }); })
      .catch((e) => { if (!cancelled) setSecurityState({ data: null, error: e.message || String(e), loading: false }); });

    setUsageState({ data: null, error: null, loading: true });
    fetch('/api/usage/portfolio')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.hint || j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { if (!cancelled) setUsageState({ data: j, error: null, loading: false }); })
      .catch((e) => { if (!cancelled) setUsageState({ data: null, error: e.message || String(e), loading: false }); });

    setReachState({ data: null, error: null, loading: true });
    fetch('/api/reachability/status')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.hint || j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { if (!cancelled) setReachState({ data: j, error: null, loading: false }); })
      .catch((e) => { if (!cancelled) setReachState({ data: null, error: e.message || String(e), loading: false }); });

    setComplianceState({ data: null, error: null, loading: true });
    fetch('/api/compliance/status')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.hint || j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { if (!cancelled) setComplianceState({ data: j, error: null, loading: false }); })
      .catch((e) => { if (!cancelled) setComplianceState({ data: null, error: e.message || String(e), loading: false }); });

    // 영역 D — 관련 Projects. 같은 분리 패턴 — 실패해도 페이지 전체 막지 않음.
    getProjectsBoard()
      .then((data) => {
        if (cancelled) return;
        setBoard({
          items: data.items || [],
          loading: false,
          error: null,
          progressAvailable: data.progressAvailable,
          progressError: data.progressError,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setBoard((prev) => ({ ...prev, loading: false, error: e.message || String(e) }));
      });
    getProductInventory()
      .then((inv) => {
        if (cancelled) return;
        setAllProducts(inv.products || []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  const product = useMemo(() => allProducts.find((p) => p.id === id), [allProducts, id]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  // US-007 — 잘못된 prd-code
  if (!product) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} component={RouterLink} to="/products" sx={{ mb: 2 }}>
          제품 목록
        </Button>
        <Alert severity="warning">
          제품을 찾을 수 없습니다: <code>{id}</code>
          <Box sx={{ mt: 1 }}>
            <Link component={RouterLink} to="/products">제품 목록으로 돌아가기</Link>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/products')}
        sx={{ mb: 2, color: 'text.secondary' }}
        size="small"
      >
        제품 목록
      </Button>

      {/* 헤더 — 메타 (US-002) */}
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.secondary' }}>
          {product.id}
        </Typography>
        <Typography variant="h1" sx={{ fontSize: '1.75rem' }}>{product.name}</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        <Chip label={product.status || '?'} color={statusColor[product.status] || 'default'} size="small" />
        <Chip label={`👤 ${product.owner || 'TBD'}`} size="small" variant="outlined" />
        {product.category && (
          <Chip label={categoryLabel[product.category] || product.category} size="small" variant="outlined" />
        )}
      </Stack>
      {product.description && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          {product.description}
        </Typography>
      )}
      <Link
        href={`https://github.com/rayrndsw/${product.id}`}
        target="_blank"
        rel="noopener"
        underline="hover"
        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
      >
        🔗 GitHub 저장소
      </Link>
      {product.notes && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1, fontStyle: 'italic' }}>
          {product.notes}
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* FEAT-020 — 제품 현황을 축별 탭으로.
          🔴 각 탭은 '요약 + 세부' 다. 요약만으로는 조치할 수 없다 — "몇 %" 가 아니라
             "무엇이", "몇 건" 이 아니라 "어디가" 를 보여야 담당자가 움직인다.
          🔴 탭 전환은 이미 받아 온 데이터로만 이뤄진다. 탭마다 새로 조회하지 않는다.
          🔴 축마다 조회가 분리돼 있어 한 축이 실패해도 다른 탭은 정상 동작한다. */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {TABS.map((t) => (
          <Tab key={t} label={t} sx={{ minHeight: 44 }} />
        ))}
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          <ProductSecurityCard productId={id} state={securityState} />
          <SecurityAlertList productId={id} state={securityState} />
          {/* 🔴 같은 '보안' 탭이지만 다른 질문에 답한다 —
                 위: Dependabot 이 본 취약점이 몇 건인가 (세는 것)
                 아래: 그걸 세는 대상이 맞게 잡혀 있는가 (세는 범위) */}
          <ProductScopeCard productId={id} />
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          <ProductComplianceCard productId={id} state={complianceState} />
          <ComplianceFailList productId={id} state={complianceState} />
        </Stack>
      )}

      {tab === 2 && (
        <Stack spacing={2}>
          <ProductUsageCard productId={id} state={usageState} />
          <UsageCountryList productId={id} state={usageState} />
        </Stack>
      )}

      {tab === 3 && <ProductReachabilityTab productId={id} state={reachState} />}

      {tab === 4 && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            제품 SSOT(products.yaml)의 hosts 목록입니다. 편집은 SSOT 에서 합니다.
          </Typography>
          <ServiceHostsList hosts={product.hosts} />
        </Stack>
      )}

      {tab === 5 && (
        <RelatedProjectsList
          productId={id}
          items={board.items}
          loading={board.loading}
          error={board.error}
          progressAvailable={board.progressAvailable}
          progressError={board.progressError}
        />
      )}

      {tab === 6 && <ProductReleasesTab productId={id} />}
    </Box>
  );
}
